import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { superAdminAPI } from '../../services/api';
import SelectMenu from '../ui/SelectMenu';

const REGIONS = [
  { value: 'northeast', label: 'Northeast' },
  { value: 'southeast', label: 'Southeast' },
  { value: 'midwest', label: 'Midwest' },
  { value: 'southwest', label: 'Southwest' },
  { value: 'west', label: 'West' },
  { value: 'northwest', label: 'Northwest' },
];

/**
 * AddAccountPage — Super-admin form to provision a new dealer/tenant account.
 * On submit, calls superAdminAPI.addDealer() which snapshots master defaults.
 */
export default function AddAccountPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // VOCAB (new canon per 377_ §2 — no prior spellings in repo, grep verified):
  //   branch_order_email     · dealer's central email for order notifications
  //                            (G-47 Lindsey). Unblocks Material List send flow
  //                            (G-34) later without a second migration.
  //   sales_rep_name         · human name of the dealer's assigned sales rep
  //                            (G-48 Lindsey). NOT the sales_rep user-ROLE enum
  //                            (RequestSeatModal.js:9, OnboardingWizard.js:1536)
  //                            which governs seat/invite flow — this is a plain
  //                            contact field with a namespaced key.
  //   sales_rep_vp_number    · numeric identifier per G-48 (Lindsey).
  const [form, setForm] = useState({
    company_name: '',
    owner_email: '',
    paid_seats: 1,
    region: '',
    // PR-64 §3 (G-47 / G-48 / G-49): optional dealer-contact fields.
    branch_order_email: '',
    sales_rep_name: '',
    sales_rep_vp_number: '',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!form.company_name.trim()) { setError('Company name is required.'); return; }
    if (!form.owner_email.trim()) { setError('Owner email is required.'); return; }
    if (!form.region) { setError('Region is required.'); return; }
    if (form.paid_seats < 1) { setError('Paid seats must be at least 1.'); return; }

    setSaving(true);
    try {
      await superAdminAPI.addDealer({
        name: form.company_name.trim(),
        business_name: form.company_name.trim(),
        email: form.owner_email.trim(),
        paid_seats: form.paid_seats,
        regionId: form.region,
        status: 'active',
        active: true,
        // PR-64 §3 (G-47 / G-48 / G-49): three optional dealer-contact fields.
        // superAdminAPI.addDealer → POST /v1/settings?action=add_list_item with
        // list_type: 'dealers' (services/api.js:452). The generic settings-list
        // mechanism accepts additional keys — no backend change required.
        // Trim to normalize; empty strings are OK (fields are optional per G-49).
        branch_order_email: form.branch_order_email.trim(),
        sales_rep_name: form.sales_rep_name.trim(),
        sales_rep_vp_number: form.sales_rep_vp_number.trim(),
      });
      navigate('/super-admin/dealers');
    } catch (err) {
      setError(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-lg mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate('/super-admin/dealers')}
        className="flex items-center gap-1 text-sm mb-6"
        style={{ color: 'var(--color-action-primary)' }}
      >
        <ArrowLeftIcon className="h-4 w-4" /> Back to Contractors
      </button>

      <h1 className="text-lg font-semibold mb-1" style={{ font: 'var(--text-h2)', color: 'var(--color-text-primary)' }}>
        Add New Account
      </h1>
      <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>
        Provision a new contractor tenant with seed defaults applied.
      </p>

      {error && (
        <div className="mb-4 p-3 text-sm border border-red-200 bg-red-50" style={{ borderRadius: 'var(--radius-md)', color: 'var(--color-text-default)' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            className="w-full border border-[color:var(--color-border)] px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            style={{ borderRadius: 'var(--radius-md)' }}
            placeholder="Acme Painting LLC"
          />
        </div>

        {/* Owner Email */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Owner Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={form.owner_email}
            onChange={(e) => handleChange('owner_email', e.target.value)}
            className="w-full border border-[color:var(--color-border)] px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            style={{ borderRadius: 'var(--radius-md)' }}
            placeholder="owner@example.com"
          />
        </div>

        {/* Paid Seats */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Paid Seats <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={form.paid_seats}
            onChange={(e) => handleChange('paid_seats', parseInt(e.target.value, 10) || 1)}
            className="w-32 border border-[color:var(--color-border)] px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            style={{ borderRadius: 'var(--radius-md)' }}
          />
        </div>

        {/* PR-64 §3: three new optional fields inserted between Paid Seats and Region.
            Match the existing repeated-boilerplate pattern in this file — no shared
            <Input> component here, keep the shape consistent so a future extraction
            captures ALL fields at once, not a subset. */}

        {/* Branch Order Email — G-47 (Lindsey) */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Branch Order Email
          </label>
          <input
            type="email"
            value={form.branch_order_email}
            onChange={(e) => handleChange('branch_order_email', e.target.value)}
            className="w-full border border-[color:var(--color-border)] px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            style={{ borderRadius: 'var(--radius-md)' }}
            placeholder="orders@example.com"
          />
        </div>

        {/* Sales Rep Name — G-48 (Lindsey) */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Sales Rep Name
          </label>
          <input
            type="text"
            value={form.sales_rep_name}
            onChange={(e) => handleChange('sales_rep_name', e.target.value)}
            className="w-full border border-[color:var(--color-border)] px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            style={{ borderRadius: 'var(--radius-md)' }}
            placeholder="Assigned sales rep"
          />
        </div>

        {/* Sales Rep VP # — G-48 (Lindsey) */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Sales Rep VP #
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={form.sales_rep_vp_number}
            onChange={(e) => handleChange('sales_rep_vp_number', e.target.value)}
            className="w-full border border-[color:var(--color-border)] px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            style={{ borderRadius: 'var(--radius-md)' }}
            placeholder="VP identifier"
          />
        </div>

        {/* Region */}
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Region <span className="text-red-500">*</span>
          </label>
          <SelectMenu
            value={form.region}
            onChange={(val) => handleChange('region', val)}
            options={REGIONS}
            placeholder="Select region..."
          />
        </div>

        {/* Submit */}
        <div className="pt-4 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-action-primary)', borderRadius: 'var(--radius-md)' }}
          >
            {saving ? 'Creating...' : 'Create Account'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/super-admin/dealers')}
            className="px-4 py-2 text-sm font-medium border border-[color:var(--color-border)]"
            style={{ borderRadius: 'var(--radius-md)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
